import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import EditIcon from "@mui/icons-material/Edit";
import DetailModal from "../../../component/common/DetailModal";
import FormModal from "../../../component/common/FormModal";
import ResizableTable from "../../../component/common/ResizableTable";
import LoadingDots from "../../../component/common/LoadingDots";
import { hrApi } from "../api/hrApi";
import { runApiAction } from "../../../util/apiFeedback";
import { approvalApi } from "../../approval/api/approvalApi";
import { ApprovalDetail } from "../../../component/approvals/ApprovalIntegrationPanel";
import { ds } from "../../../styles/designSystem";
import { today as getToday } from "../../../util/dateUtils";
import FormField from "../../../component/common/FormField";
import ActionButton from "../../../component/common/ActionButton";
import ComboBox from "../../../component/common/ComboBox";
import SearchField from "../../../component/common/SearchField";
import { useTableSearch } from "../../../util/tableSearch";

const currentYear = getToday().year();

const STATUS_META = {
    PENDING: { label: "대기", color: "#94a3b8", bg: "#f1f5f9" },
    IN_PROGRESS: { label: "진행중", color: "#2563eb", bg: "#eff6ff" },
    APPROVED: { label: "승인", color: "#16a34a", bg: "#f0fdf4" },
    REJECTED: { label: "반려", color: "#dc2626", bg: "#fef2f2" },
    HELD: { label: "보류", color: "#d97706", bg: "#fffbeb" },
};

const fmtDays = (days) => {
    if (days == null) return "—";
    return `${Number(days).toFixed(Number(days) % 1 === 0 ? 0 : 1)}일`;
};

// 시간(정수) → "N일 M시간" 표기 (1일 = 8시간)
const HOURS_PER_DAY = 8;
const toDayHour = (h) => {
    if (h == null) return "—";
    const n = Number(h);
    const d = Math.floor(n / HOURS_PER_DAY);
    const r = n % HOURS_PER_DAY;
    if (d > 0 && r > 0) return `${d}일 ${r}시간`;
    if (d > 0) return `${d}일`;
    return `${r}시간`;
};

function UsageDetailDialog({ open, onClose, employee, year }) {
    const [usageList, setUsageList]             = useState([]);
    const [approvalDetail, setApprovalDetail]   = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);

    useEffect(() => {
        if (!open || !employee) return;
        hrApi.getLeaveUsage(employee.employeeId, year).then(setUsageList).catch(() => setUsageList([]));
    }, [open, employee, year]);

    const handleOpenApproval = async (approvalId) => {
        if (!approvalId) return;
        setApprovalLoading(true);
        try {
            const res = await approvalApi.detail(approvalId);
            setApprovalDetail(res);
        } catch {
            // ignore
        } finally {
            setApprovalLoading(false);
        }
    };

    const tcCenter = { align: "center", sx: { textAlign: "center" } };

    return (
        <>
            <FormModal
                open={open}
                onClose={onClose}
                title={`${employee?.employeeName || ""} · ${year}년 휴가 사용 근거`}
                maxWidth="md"
                cancelLabel="닫기"
            >
                    {usageList.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: "center", color: "#94a3b8" }}>사용 내역이 없습니다.</Box>
                    ) : (
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                    <TableCell align="center">유형</TableCell>
                                    <TableCell align="center">기간</TableCell>
                                    <TableCell align="center">사용량</TableCell>
                                    <TableCell align="center">결재 제목</TableCell>
                                    <TableCell align="center">상태</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {usageList.map((usage) => {
                                    const statusMeta = usage.approvalStatus
                                        ? (STATUS_META[usage.approvalStatus] || STATUS_META.APPROVED) : null;
                                    const period = usage.startDate
                                        ? (usage.endDate && usage.endDate !== usage.startDate
                                            ? `${usage.startDate} ~ ${usage.endDate}` : usage.startDate)
                                        : "—";
                                    const hasApproval = Boolean(usage.approvalId);
                                    return (
                                        <TableRow key={usage.id} hover>
                                            <TableCell align="center">{usage.leaveTypeLabel}</TableCell>
                                            <TableCell align="center">{period}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>{usage.hours}시간 <Box component="span" sx={{ color: "#94a3b8", fontWeight: 400 }}>({toDayHour(usage.hours)})</Box></TableCell>
                                            <TableCell align="center">
                                                {hasApproval ? (
                                                    <ActionButton
                                                        variant="secondary"
                                                        startIcon={false}
                                                        disabled={approvalLoading}
                                                        onClick={() => handleOpenApproval(usage.approvalId)}
                                                        label={usage.approvalTitle || "결재 보기"}
                                                        sx={{ fontSize: 12, color: "#2563eb",
                                                            textDecoration: "underline", p: 0, minWidth: 0,
                                                            border: "none", "&:hover": { border: "none", bgcolor: "transparent" } }}
                                                    />
                                                ) : (
                                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                                                        {usage.note || "—"}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                {statusMeta ? (
                                                    <Chip label={statusMeta.label} size="small"
                                                        sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700 }} />
                                                ) : "—"}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </ResizableTable>
                    )}
            </FormModal>

            {/* 결재 상세 모달 */}
            <DetailModal
                open={Boolean(approvalDetail)}
                onClose={() => setApprovalDetail(null)}
                title="결재 상세"
            >
                {approvalLoading ? (
                    <LoadingDots compact />
                ) : approvalDetail && (
                    <ApprovalDetail
                        approval={approvalDetail}
                        myId={null}
                        onRefresh={() => {}}
                        onDelete={null}
                    />
                )}
            </DetailModal>
        </>
    );
}

/* 대체휴무 근거 — 지급(휴일근무 적립) / 사용 탭 */
function CompDayDetailDialog({ open, onClose, employee, year }) {
    const [tabIdx, setTabIdx]                   = useState(0);
    const [usageList, setUsageList]             = useState([]);
    const [grantList, setGrantList]             = useState([]);
    const [approvalDetail, setApprovalDetail]   = useState(null);
    const [approvalLoading, setApprovalLoading] = useState(false);

    useEffect(() => {
        if (!open || !employee) return;
        setTabIdx(0);
        hrApi.getLeaveUsage(employee.employeeId, year)
            .then((list) => setUsageList((Array.isArray(list) ? list : []).filter((u) => u.leaveType === "COMP_DAY")))
            .catch(() => setUsageList([]));
        hrApi.getCompGrants(employee.employeeId, year)
            .then((list) => setGrantList(Array.isArray(list) ? list : []))
            .catch(() => setGrantList([]));
    }, [open, employee, year]);

    const openApproval = async (id) => {
        if (!id) return;
        setApprovalLoading(true);
        try { setApprovalDetail(await approvalApi.detail(id)); }
        catch { /* ignore */ }
        finally { setApprovalLoading(false); }
    };
    const linkBtn = (id, label) => (
        <ActionButton variant="secondary" startIcon={false} disabled={approvalLoading}
            onClick={() => openApproval(id)} label={label || "결재 보기"}
            sx={{ fontSize: 12, color: "#2563eb", textDecoration: "underline", p: 0, minWidth: 0,
                border: "none", "&:hover": { border: "none", bgcolor: "transparent" } }} />
    );

    return (
        <>
            <FormModal open={open} onClose={onClose}
                title={`${employee?.employeeName || ""} · ${year}년 대체휴무 근거`}
                maxWidth="md" cancelLabel="닫기">
                <Tabs value={tabIdx} onChange={(_, v) => setTabIdx(v)} sx={ds.components.tabs.compact}>
                    <Tab label={`지급(적립) ${grantList.length}`} />
                    <Tab label={`사용 ${usageList.length}`} />
                </Tabs>

                {tabIdx === 0 ? (
                    grantList.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: "center", color: "#94a3b8" }}>적립(휴일근무) 내역이 없습니다.</Box>
                    ) : (
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead><TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell align="center">근무일</TableCell>
                                <TableCell align="center">근무시간</TableCell>
                                <TableCell align="center">결재</TableCell>
                                <TableCell align="center">상태</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {grantList.map((g) => {
                                    const d = g.form || {};
                                    const sm = STATUS_META[g.status] || STATUS_META.APPROVED;
                                    return (
                                        <TableRow key={g.id} hover>
                                            <TableCell align="center">{d.date || "—"}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>{d.hours ? `${d.hours}시간` : "—"}</TableCell>
                                            <TableCell align="center">{linkBtn(g.id, g.title)}</TableCell>
                                            <TableCell align="center"><Chip label={sm.label} size="small" sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 700 }} /></TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </ResizableTable>
                    )
                ) : (
                    usageList.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: "center", color: "#94a3b8" }}>사용 내역이 없습니다.</Box>
                    ) : (
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead><TableRow sx={{ bgcolor: "#f8fafc" }}>
                                <TableCell align="center">기간</TableCell>
                                <TableCell align="center">사용시간</TableCell>
                                <TableCell align="center">결재</TableCell>
                                <TableCell align="center">상태</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {usageList.map((u) => {
                                    const sm = u.approvalStatus ? (STATUS_META[u.approvalStatus] || STATUS_META.APPROVED) : null;
                                    const period = u.startDate
                                        ? (u.endDate && u.endDate !== u.startDate ? `${u.startDate} ~ ${u.endDate}` : u.startDate)
                                        : "—";
                                    return (
                                        <TableRow key={u.id} hover>
                                            <TableCell align="center">{period}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 700 }}>{u.hours}시간</TableCell>
                                            <TableCell align="center">
                                                {u.approvalId ? linkBtn(u.approvalId, u.approvalTitle)
                                                    : <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{u.note || "—"}</Typography>}
                                            </TableCell>
                                            <TableCell align="center">{sm ? <Chip label={sm.label} size="small" sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 700 }} /> : "—"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </ResizableTable>
                    )
                )}
            </FormModal>

            <DetailModal open={Boolean(approvalDetail)} onClose={() => setApprovalDetail(null)} title="결재 상세">
                {approvalLoading ? <LoadingDots compact /> : approvalDetail && (
                    <ApprovalDetail approval={approvalDetail} myId={null} onRefresh={() => {}} onDelete={null} />
                )}
            </DetailModal>
        </>
    );
}

export default function HrLeaveTab() {
    const [year, setYear] = useState(currentYear);
    const [allowances, setAllowances] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editTarget, setEditTarget] = useState(null);
    const [usageTarget, setUsageTarget] = useState(null);
    const [compTarget, setCompTarget] = useState(null);
    const [form, setForm] = useState({
        employeeId: "",
        annualGranted: 0,
        annualCarryOver: 0,
        annualAdjusted: 0,
        sickGranted: 0,
        sickAdjusted: 0,
        compDayGranted: 0,
        compDayAdjusted: 0,
        grantBaseDate: "",
        note: "",
    });

    const [sort, setSort] = useState({ key: "employeeName", dir: "asc" });
    const [search, setSearch] = useState("");   // 휴가 원장 검색(이름·부서·메모·기준일)

    const toggleSort = (key) =>
        setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

    const sortLabel = (key, label) =>
        `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;

    const sortedAllowances = useMemo(() => {
        return [...allowances].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv), "ko");
            return sort.dir === "asc" ? cmp : -cmp;
        });
    }, [allowances, sort]);

    // 원장 전량을 클라이언트에 들고 있으므로(페이지네이션 아님) 지정 필드 부분일치로 안전하게 검색.
    const visibleAllowances = useTableSearch(sortedAllowances, search,
        ["employeeName", "deptName", "note", "grantBaseDate"]);

    const load = () => {
        setLoading(true);
        Promise.allSettled([
            hrApi.getLeaveAllowances().then(setAllowances).catch(() => {}),
            hrApi.getEmployees({ size: 9999 }).then(res => setEmployees(res?.content ?? (Array.isArray(res) ? res : []))).catch(() => {}),
        ]).finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const openEditDialog = (row) => {
        setEditTarget(row);
        setForm({
            employeeId: row.employeeId,
            annualGranted: row.annualGranted || 0,
            annualCarryOver: row.annualCarryOver || 0,
            annualAdjusted: row.annualAdjusted || 0,
            sickGranted: row.sickGranted || 0,
            sickAdjusted: row.sickAdjusted || 0,
            compDayGranted: row.compDayGranted || 0,
            compDayAdjusted: row.compDayAdjusted || 0,
            grantBaseDate: row.grantBaseDate || "",
            note: row.note || "",
        });
    };

    const handleSave = () => runApiAction({
        request: () => hrApi.saveLeaveAllowance({
            ...form,
            employeeId: Number(form.employeeId),
            annualGranted: Number(form.annualGranted),
            annualCarryOver: Number(form.annualCarryOver),
            annualAdjusted: Number(form.annualAdjusted),
            sickGranted: Number(form.sickGranted),
            sickAdjusted: Number(form.sickAdjusted),
            compDayAdjusted: Number(form.compDayAdjusted),
        }),
        onSuccess: () => { setEditTarget(null); load(); },
    });

    const years = [currentYear - 1, currentYear, currentYear + 1];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper sx={ds.card}>
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", gap: 2, alignItems: "center" }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>휴가 원장</Typography>
                    <SearchField value={search} onChange={setSearch} placeholder="이름·부서·메모 검색" width={240} />
                    <Box sx={{ width: 100 }}>
                        <ComboBox clearable={false} placeholder="연도"
                            options={years.map((item) => ({ value: item, label: `${item}년` }))}
                            value={year}
                            onChange={(v) => setYear(Number(v))} />
                    </Box>
                </Box>
                <Divider />
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("employeeName")}>{sortLabel("employeeName", "이름")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("annualRemaining")}>{sortLabel("annualRemaining", "연차")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("sickRemaining")}>{sortLabel("sickRemaining", "병가")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("compDayRemaining")}>{sortLabel("compDayRemaining", "대체휴무")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("grantBaseDate")}>{sortLabel("grantBaseDate", "기준일")}</TableCell>
                            <TableCell>메모</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleAllowances.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    {search.trim() ? "검색 결과가 없습니다." : "데이터 없음"}
                                </TableCell>
                            </TableRow>
                        )}
                        {visibleAllowances.map((row) => (
                            <TableRow key={row.id} hover sx={{ cursor: "pointer" }}
                                onClick={() => openEditDialog(row)}>
                                <TableCell sx={{ fontWeight: 700 }}>{row.employeeName}</TableCell>
                                <TableCell>{row.deptName || "—"}</TableCell>
                                <TableCell align="center">
                                    <LeaveBalanceCell
                                        total={row.annualTotal}
                                        used={row.annualUsed}
                                        remaining={row.annualRemaining}
                                        onClick={() => setUsageTarget({ employeeId: row.employeeId, employeeName: row.employeeName })}
                                    />
                                </TableCell>
                                <TableCell align="center"><LeaveSimpleCell total={row.sickTotal} used={row.sickUsed} remaining={row.sickRemaining} /></TableCell>
                                <TableCell align="center">
                                    <LeaveBalanceCell
                                        total={row.compDayTotal}
                                        used={row.compDayUsed}
                                        remaining={row.compDayRemaining}
                                        onClick={() => setCompTarget({ employeeId: row.employeeId, employeeName: row.employeeName })}
                                    />
                                </TableCell>
                                <TableCell>{row.grantBaseDate || "—"}</TableCell>
                                <TableCell sx={{ color: "#64748b", fontSize: 12 }}>{row.note || "—"}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </ResizableTable>}
            </Paper>

            <FormModal
                open={Boolean(editTarget)}
                onClose={() => setEditTarget(null)}
                title={`휴가 원장 수정${editTarget ? ` — ${editTarget.employeeName}` : ""}`}
                maxWidth="sm"
                onSave={handleSave}
                saveDisabled={!form.employeeId}
            >
                <Stack spacing={2}>
                    <FormField label="직원" required>
                        <ComboBox clearable={false} placeholder="직원"
                            options={employees.map((employee) => ({ value: employee.id, label: `${employee.name} (${employee.deptName})` }))}
                            value={form.employeeId}
                            onChange={(v) => setForm((prev) => ({ ...prev, employeeId: v }))} />
                    </FormField>
                    <FormField label="기준일">
                        <TextField size="small" type="date" value={form.grantBaseDate} onChange={(e) => setForm((prev) => ({ ...prev, grantBaseDate: e.target.value }))} />
                    </FormField>
                    <AllowanceRow
                        title="연차"
                        granted={form.annualGranted}
                        carryOver={form.annualCarryOver}
                        adjusted={form.annualAdjusted}
                        onGrantedChange={(value) => setForm((prev) => ({ ...prev, annualGranted: value }))}
                        onCarryOverChange={(value) => setForm((prev) => ({ ...prev, annualCarryOver: value }))}
                        onAdjustedChange={(value) => setForm((prev) => ({ ...prev, annualAdjusted: value }))}
                    />
                    <AllowanceSimpleRow
                        title="병가"
                        granted={form.sickGranted}
                        adjusted={form.sickAdjusted}
                        onGrantedChange={(value) => setForm((prev) => ({ ...prev, sickGranted: value }))}
                        onAdjustedChange={(value) => setForm((prev) => ({ ...prev, sickAdjusted: value }))}
                    />
                    <AllowanceCompRow
                        title="대체휴무"
                        granted={form.compDayGranted}
                        adjusted={form.compDayAdjusted}
                        onAdjustedChange={(value) => setForm((prev) => ({ ...prev, compDayAdjusted: value }))}
                    />
                    <FormField label="메모">
                        <TextField size="small" value={form.note} onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))} placeholder="메모를 입력하세요" />
                    </FormField>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        사용량과 잔여량은 저장하지 않고, 승인된 결재 문서를 근거로 자동 계산됩니다.
                    </Alert>
                </Stack>
            </FormModal>

            <UsageDetailDialog
                open={Boolean(usageTarget)}
                onClose={() => setUsageTarget(null)}
                employee={usageTarget}
                year={year}
            />

            <CompDayDetailDialog
                open={Boolean(compTarget)}
                onClose={() => setCompTarget(null)}
                employee={compTarget}
                year={year}
            />
        </Box>
    );
}

function LeaveBalanceCell({ total, used, remaining, onClick }) {
    return (
        <Box sx={{ cursor: "pointer" }} onClick={(ev) => { ev.stopPropagation(); onClick?.(ev); }}>
            <Typography sx={{ fontSize: 12, lineHeight: 1.2, fontWeight: 700, color: remaining <= 8 ? "#dc2626" : "#0f172a" }}>{toDayHour(remaining)}</Typography>
            <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, color: "#94a3b8" }}>잔여 {remaining}시간 · 사용 {used}/{total}시간</Typography>
        </Box>
    );
}

function LeaveSimpleCell({ total, used, remaining }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, lineHeight: 1.2, fontWeight: 700, color: remaining <= 0 ? "#dc2626" : "#0f172a" }}>{toDayHour(remaining)}</Typography>
            <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, color: "#94a3b8" }}>잔여 {remaining}시간 · 사용 {used}/{total}시간</Typography>
        </Box>
    );
}

function AllowanceRow({ title, granted, carryOver, adjusted, onGrantedChange, onCarryOverChange, onAdjustedChange }) {
    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <FormField label={`${title} 부여`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 0.5 }} value={granted} onChange={(e) => onGrantedChange(e.target.value)} />
            </FormField>
            <FormField label={`${title} 이월`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 0.5 }} value={carryOver} onChange={(e) => onCarryOverChange(e.target.value)} />
            </FormField>
            <FormField label={`${title} 조정`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 0.5 }} value={adjusted} onChange={(e) => onAdjustedChange(e.target.value)} />
            </FormField>
        </Box>
    );
}

function AllowanceSimpleRow({ title, granted, adjusted, onGrantedChange, onAdjustedChange }) {
    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <FormField label={`${title} 부여`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 0.5 }} value={granted} onChange={(e) => onGrantedChange(e.target.value)} />
            </FormField>
            <FormField label={`${title} 조정`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 0.5 }} value={adjusted} onChange={(e) => onAdjustedChange(e.target.value)} />
            </FormField>
        </Box>
    );
}

/* 대체휴무 — 적립(휴일근무 자동, 읽기 전용) + 조정(수동) */
function AllowanceCompRow({ title, granted, adjusted, onAdjustedChange }) {
    return (
        <Box sx={{ display: "flex", gap: 1 }}>
            <FormField label={`${title} 적립(자동)`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" value={granted} disabled
                    helperText="휴일근무 결재 승인 시 자동 적립 (시간)" />
            </FormField>
            <FormField label={`${title} 조정`} sx={{ flex: 1 }}>
                <TextField size="small" type="number" inputProps={{ step: 1 }} value={adjusted} onChange={(e) => onAdjustedChange(e.target.value)} />
            </FormField>
        </Box>
    );
}
